import { DomainError } from './errors';

/**
 * RELOJ LÓGICO HÍBRIDO (HLC) — PRD 6.6 y 6.7.
 *
 * El reloj de pared del cliente miente. Móviles con la hora puesta a mano, zonas
 * mal configuradas, relojes que se van. Usar `Date.now()` crudo para «gana el
 * último» produce escrituras del futuro que bloquean actualizaciones legítimas
 * durante días — es el bug de «se me desmarcó el hábito solo».
 *
 * El HLC combina el tiempo físico con un contador, y se serializa para que
 * **comparar dos HLC sea comparar dos cadenas**:
 *
 *     000001789249464678:00003:9f2c…
 *     └──── ms, 15 ────┘ └ c ┘ └ id ┘
 *
 * Tres detalles que hacen que eso funcione y que no son decorativos:
 *
 *  · **ms a 15 dígitos.** Con longitud fija, el orden lexicográfico coincide con
 *    el numérico. Caben milisegundos hasta el año 5138.
 *  · **contador en base 36 a 5 dígitos.** Los dígitos de base 36 son 0-9a-z, y
 *    en ASCII '0' < '9' < 'a' < 'z': con la misma longitud, el orden alfabético
 *    vuelve a ser el numérico.
 *  · **el device_id al final.** Da un desempate TOTAL y determinista: dos
 *    dispositivos que escriben en el mismo milisegundo y con el mismo contador
 *    convergen igualmente al mismo ganador, sin hablar entre ellos.
 */

export type Hlc = {
  /** Milisegundos físicos, ya reconciliados. */
  readonly ms: number;
  /** Desempate dentro del mismo milisegundo. */
  readonly counter: number;
  readonly deviceId: string;
};

const MS_DIGITS = 15;
const COUNTER_DIGITS = 5;
const COUNTER_MAX = 36 ** COUNTER_DIGITS - 1; // 60.466.175

/** Deriva máxima tolerada hacia el futuro antes de recortar (PRD 6.7). */
export const MAX_DRIFT_MS = 5 * 60 * 1000;

/**
 * A partir de aquí no es un reloj mal puesto, es un reloj roto o un ataque, y se
 * rechaza en vez de recortar.
 *
 * El umbral tiene que dejar pasar el escenario S5 —un dispositivo 3 días
 * adelantado se acepta con `attested = false`— y parar el S6 —10 años—. 30 días
 * está cómodamente entre los dos: nadie tiene el móvil un mes adelantado por
 * accidente, y si lo tiene, sus datos siguen llegando; lo que no llega es su HLC.
 */
export const IMPLAUSIBLE_DRIFT_MS = 30 * 24 * 60 * 60 * 1000;

export function serializeHlc(hlc: Hlc): string {
  const ms = String(hlc.ms).padStart(MS_DIGITS, '0');
  const counter = hlc.counter.toString(36).padStart(COUNTER_DIGITS, '0');
  return `${ms}:${counter}:${hlc.deviceId}`;
}

export function parseHlc(value: string): Hlc {
  const first = value.indexOf(':');
  const second = value.indexOf(':', first + 1);
  if (first !== MS_DIGITS || second !== first + 1 + COUNTER_DIGITS) {
    throw new DomainError('invalid-request', `HLC mal formado: ${value}`);
  }

  const ms = Number(value.slice(0, first));
  const counter = Number.parseInt(value.slice(first + 1, second), 36);
  const deviceId = value.slice(second + 1);

  if (!Number.isInteger(ms) || ms < 0 || !Number.isInteger(counter) || deviceId.length === 0) {
    throw new DomainError('invalid-request', `HLC mal formado: ${value}`);
  }
  return { ms, counter, deviceId };
}

/**
 * Comparación. Devuelve <0, 0 o >0.
 *
 * Se compara sobre la forma serializada a propósito: es exactamente lo que hace
 * Postgres en el `WHERE excluded.hlc > tabla.hlc` del UPSERT. Si aquí se
 * comparara por campos y allí por texto, cliente y servidor podrían discrepar en
 * algún borde, y sería un bug imposible de encontrar.
 */
export function compareHlc(a: Hlc | string, b: Hlc | string): number {
  const left = typeof a === 'string' ? a : serializeHlc(a);
  const right = typeof b === 'string' ? b : serializeHlc(b);
  return left < right ? -1 : left > right ? 1 : 0;
}

export function maxHlc(a: Hlc, b: Hlc): Hlc {
  return compareHlc(a, b) >= 0 ? a : b;
}

export function initialHlc(deviceId: string): Hlc {
  return { ms: 0, counter: 0, deviceId };
}

function bumpCounter(counter: number): number {
  if (counter >= COUNTER_MAX) {
    // 60 millones de eventos dentro del mismo milisegundo es imposible en este
    // producto. Si pasara, fallar es mejor que desbordar en silencio y romper el
    // orden lexicográfico que sostiene todo lo demás.
    throw new DomainError('internal', 'contador de HLC desbordado');
  }
  return counter + 1;
}

/**
 * Un evento local: se emite un HLC nuevo. Nunca retrocede, ni aunque el reloj
 * del dispositivo salte hacia atrás —que es lo que hace al sincronizar con NTP—.
 */
export function tickHlc(local: Hlc, physicalMs: number): Hlc {
  const ms = Math.max(local.ms, Math.trunc(physicalMs));
  return ms === local.ms
    ? { ms, counter: bumpCounter(local.counter), deviceId: local.deviceId }
    : { ms, counter: 0, deviceId: local.deviceId };
}

/**
 * Llega un evento remoto: el reloj local aprende de él. Es lo que hace que dos
 * dispositivos que nunca se han visto acaben con relojes comparables.
 */
export function receiveHlc(local: Hlc, remote: Hlc, physicalMs: number): Hlc {
  const physical = Math.trunc(physicalMs);
  const ms = Math.max(local.ms, remote.ms, physical);

  let counter: number;
  if (ms === local.ms && ms === remote.ms)
    counter = bumpCounter(Math.max(local.counter, remote.counter));
  else if (ms === local.ms) counter = bumpCounter(local.counter);
  else if (ms === remote.ms) counter = bumpCounter(remote.counter);
  else counter = 0;

  return { ms, counter, deviceId: local.deviceId };
}

export type ClampOutcome =
  | { kind: 'ok'; hlc: Hlc; attested: true }
  | { kind: 'clamped'; hlc: Hlc; attested: false; driftMs: number };

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL CLAMP. Cinco líneas de verdad, y el punto individual más crítico del
 * sistema entero (PRD 6.7, [R3]).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un dispositivo con el reloj diez años adelantado manda un HLC que, si se
 * acepta, **envenena el reloj lógico de todo el sistema de forma
 * irrecuperable**: a partir de ahí ninguna escritura futura podría superarlo
 * nunca, y no hay migración que lo arregle porque el valor ya está en las filas.
 *
 * Por eso esto corre SIEMPRE en el servidor, nunca en el cliente: el cliente es
 * justamente quien tiene el reloj sospechoso.
 *
 * Tres caminos:
 *   · deriva ≤ 5 min        → se acepta tal cual. `attested = true`
 *   · deriva ≤ 30 días      → se RECORTA a ahora + 5 min y `attested = false`.
 *                             El dato del usuario se guarda; lo que no se
 *                             guarda es su reloj. La progresión no cuenta lo no
 *                             atestado, así que un reloj adelantado no da puntos.
 *   · más                   → se RECHAZA con `implausible-clock`, que es un
 *                             error NO reintentable: la mutación sale de la cola
 *                             en vez de reintentarse para siempre.
 *
 * Verificado en los escenarios S5 y S6 del apartado 17.2.
 */
export function clampIncomingHlc(remote: Hlc, serverNowMs: number): ClampOutcome {
  const ceiling = serverNowMs + MAX_DRIFT_MS;
  if (remote.ms <= ceiling) {
    return { kind: 'ok', hlc: remote, attested: true };
  }

  const driftMs = remote.ms - serverNowMs;
  if (driftMs > IMPLAUSIBLE_DRIFT_MS) {
    throw new DomainError(
      'implausible-clock',
      `el reloj del dispositivo va ${Math.round(driftMs / 86_400_000)} días adelantado`,
    );
  }

  return {
    kind: 'clamped',
    hlc: { ms: ceiling, counter: remote.counter, deviceId: remote.deviceId },
    attested: false,
    driftMs,
  };
}

/**
 * HLC POR CAMPO, no por fila (PRD 6.6).
 *
 * Si un dispositivo cambia `status` y otro cambia `value`, un «gana el último»
 * por fila pierde uno de los dos cambios. Con dos o tres campos mutables el
 * coste de guardar un HLC por campo es despreciable y elimina una clase entera
 * de bugs. Se guarda como `jsonb`.
 */
export type FieldClock = Record<string, string>;

export type MergeResult<T> = {
  readonly value: T;
  readonly clock: FieldClock;
  /** Campos que la escritura entrante ganó. Vacío = no cambió nada. */
  readonly applied: readonly string[];
};

/**
 * Mezcla campo a campo quedándose con el HLC mayor de cada uno.
 *
 * Un campo que el entrante NO trae se conserva tal cual: una actualización
 * parcial no borra lo que no menciona.
 */
export function mergeFields<T extends Record<string, unknown>>(
  current: T,
  currentClock: FieldClock,
  incoming: Partial<T>,
  incomingHlc: string,
): MergeResult<T> {
  const value = { ...current };
  const clock = { ...currentClock };
  const applied: string[] = [];

  for (const field of Object.keys(incoming)) {
    const existing = clock[field];
    // Sin HLC previo el campo nunca se escribió: gana el entrante.
    if (existing !== undefined && compareHlc(incomingHlc, existing) <= 0) continue;

    (value as Record<string, unknown>)[field] = incoming[field];
    clock[field] = incomingHlc;
    applied.push(field);
  }

  return { value, clock, applied };
}
